import { Button } from '../../components/Button';
import {
  type PaginationLabels,
  paginationLabelVariants,
} from '../../components/Pagination';
import { Skeleton } from '../../components/Skeleton';
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from '../../icons';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';

/** Pagination's 20px chevron in its 40px (`m`) chip — the same override. */
const ICON_SIZE = 20;

export interface PaginationSkeletonProps {
  /** Pre-translated labels for the four (disabled) chevron buttons. */
  labels?: PaginationLabels;
  className?: string;
  testID?: string;
}

/**
 * The loading twin of the DS `Pagination` under a card that is still on its
 * way: the same disabled chevron buttons and the same label chip, with a grey
 * bar (breathing in step with the card) where "3/12" will read — never a
 * spinner (spinners only live in a button that is working). Pairs with
 * `FlashcardSkeleton`.
 */
export function PaginationSkeleton({
  labels,
  className,
  testID,
}: PaginationSkeletonProps) {
  return (
    <View className={cn('w-full flex-row items-center gap-2', className)} testID={testID}>
      <Button
        variant="secondary"
        size="m"
        icon={ChevronFirst}
        iconSize={ICON_SIZE}
        disabled
        accessibilityLabel={labels?.first ?? 'First page'}
      />
      <Button
        variant="secondary"
        size="m"
        icon={ChevronLeft}
        iconSize={ICON_SIZE}
        disabled
        accessibilityLabel={labels?.previous ?? 'Previous page'}
      />
      <View className={paginationLabelVariants()}>
        <Skeleton shape="line" className="w-12" />
      </View>
      <Button
        variant="secondary"
        size="m"
        icon={ChevronRight}
        iconSize={ICON_SIZE}
        disabled
        accessibilityLabel={labels?.next ?? 'Next page'}
      />
      <Button
        variant="secondary"
        size="m"
        icon={ChevronLast}
        iconSize={ICON_SIZE}
        disabled
        accessibilityLabel={labels?.last ?? 'Last page'}
      />
    </View>
  );
}
(PaginationSkeleton as { displayName?: string }).displayName = 'PaginationSkeleton';
